import React, { useCallback } from 'react'

import { FilterBoxItemState, UiFilterIcon } from '@octane/spark'
import { ManagedDealership } from 'api/dealership/types'
import useManagedDealerships from 'hooks/useManagedDealerships'
import useLeadsPageContext from 'pages/LeadsPage/LeadsPageContext'
import { ApplicationFilters } from 'pages/LeadsPage/types'
import { pushFilterSet } from 'utils/gtm-utils'

import CustomFilterBox from '../../../../containers/SavedApplicationsList/partials/CustomFilterBox/CustomFilterBox'

function buildDealershipFilterItems(
    dealerships: ManagedDealership[],
    currentFilters: ApplicationFilters,
): FilterBoxItemState[] {
    return [...dealerships]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((dealership) => ({
            value: dealership.id.toString(),
            label: dealership.name,
            checked: currentFilters.dealershipIds.includes(dealership.id),
            group: 'Dealership',
            order: 0,
        }))
}

function getApplicationsFilters(
    items: FilterBoxItemState[],
    currentFilters: ApplicationFilters,
): ApplicationFilters {
    const selectedDealershipIds = items
        .filter((item) => item.checked)
        .map((item) => parseInt(item.value, 10))

    return {
        prequalifiedLeads: currentFilters.prequalifiedLeads,
        declinedLeads: currentFilters.declinedLeads,
        preApprovedLeads: currentFilters.preApprovedLeads,
        dealershipIds: selectedDealershipIds,
    }
}

function haveDealershipIdsChanged(
    newIds: number[],
    currentIds: number[],
): boolean {
    if (newIds.length !== currentIds.length) return true

    const newSet = new Set(newIds)

    return !currentIds.every((id) => newSet.has(id))
}

function DealershipFilterBox(): JSX.Element | null {
    const { applicationFilters, setApplicationFilters } = useLeadsPageContext()
    const { dealershipIds } = applicationFilters
    const dealerships = useManagedDealerships()

    const setFilterActionCb = useCallback(
        (selectedItems: FilterBoxItemState[]) => {
            const newApplicationFilters = getApplicationsFilters(
                selectedItems,
                applicationFilters,
            )

            if (
                haveDealershipIdsChanged(
                    newApplicationFilters.dealershipIds,
                    applicationFilters.dealershipIds,
                )
            ) {
                pushFilterSet('dealership')
            }

            setApplicationFilters(newApplicationFilters)
        },
        [applicationFilters, setApplicationFilters],
    )

    const onClearButtonCb = useCallback(() => {
        setApplicationFilters({
            ...applicationFilters,
            dealershipIds: [],
        })
    }, [applicationFilters, setApplicationFilters])

    const showClearButton =
        dealershipIds.length > 0
            ? {
                  showClearButton: true,
                  clearButtonLabel: 'Clear Filters',
                  onClear: onClearButtonCb,
              }
            : {
                  showClearButton: false,
              }

    if (dealerships.length === 0) {
        return null
    }

    const dealershipFilterItems = buildDealershipFilterItems(
        dealerships,
        applicationFilters,
    )

    return (
        <CustomFilterBox
            id="leadsPage__dealershipFilter"
            multiple
            items={dealershipFilterItems}
            label="Dealership"
            icon={UiFilterIcon}
            actionFunction={setFilterActionCb}
            {...showClearButton}
        />
    )
}

export default DealershipFilterBox
